import { useMemo, type UIEvent } from 'react';

import { useSearchContext } from '@cast/shared-search-ui';
import { Cluster, ClusterDisplayStatus } from '@cast/types';

import { clustersSearchCriteria } from '../constants/searchCriteria';

export type ClustersAppliedFilterChip = {
  id: string;
  title: string;
  value: string;
  delimiter: string;
  onRemove: () => void;
};

const FILTER_CRITERION_LABELS: Record<string, string> = {
  status: 'Cluster status',
  region: 'Region',
  project: 'Project',
  provider: 'Provider',
  managed: 'Managed',
  agentStatus: 'Agent status',
};

type StatusOption = {
  group: string;
  value: ClusterDisplayStatus;
};

const isActiveFilterValue = (value: unknown) => {
  if (value == null) {
    return false;
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return true;
};

const formatFilterValue = (key: string, value: unknown): string => {
  if (key === 'status' && Array.isArray(value)) {
    return (value as StatusOption[])
      .map((option) => option.value)
      .filter(Boolean)
      .join(', ');
  }

  if (Array.isArray(value)) {
    return value.map(String).join(', ');
  }

  return String(value);
};

const getCriterionTitle = (key: string) => {
  if (key in FILTER_CRITERION_LABELS) {
    return FILTER_CRITERION_LABELS[key];
  }

  const criterion = clustersSearchCriteria.find((item) => item.key === key);
  return criterion?.title || key;
};

const getCriterionDelimiter = (key: string) => {
  if (key === 'status') {
    return '';
  }

  if (key.endsWith(':')) {
    return '';
  }

  return ':';
};

export const useClustersAppliedFilterChips = () => {
  const searchCtx = useSearchContext<Cluster>();

  return useMemo(() => {
    const values = searchCtx?.values ?? {};
    const chips: ClustersAppliedFilterChip[] = [];
    let appliedFilterCount = searchCtx?.freeText ? 1 : 0;

    if (searchCtx?.freeText) {
      chips.push({
        id: 'free-text',
        title: 'Search',
        value: searchCtx.freeText,
        delimiter: ':',
        onRemove: () => searchCtx.setFreeText?.(undefined),
      });
    }

    Object.entries(values).forEach(([key, value]) => {
      if (!isActiveFilterValue(value)) {
        return;
      }

      appliedFilterCount += 1;
      chips.push({
        id: key,
        title: getCriterionTitle(key),
        value: formatFilterValue(key, value),
        delimiter: getCriterionDelimiter(key),
        onRemove: () => searchCtx?.clearValue?.(key),
      });
    });

    return {
      chips,
      resultCount: searchCtx?.filteredData?.length ?? 0,
      appliedFilterCount,
      hasAppliedFilters: appliedFilterCount > 0,
      clearAll: (event?: UIEvent) => searchCtx?.resetInitial(event),
    };
  }, [searchCtx]);
};
